import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { catchError, finalize, map, Observable, of, switchMap, throwError, timeout } from 'rxjs';
import { Company } from 'src/app/company/company.model';
import { CompanyService } from 'src/app/company/company.service';
import { FreightForwarder } from 'src/app/company/freight-forwarder/freight-forwarder.model';
import { FreightForwarderService } from 'src/app/company/freight-forwarder/freight-forwarder.service';
import { TransportationAgency } from 'src/app/company/transportation-agency/transportation-agency.model';
import { TransportationAgencyService } from 'src/app/company/transportation-agency/transportation-agency.service';
import { CustomsClearanceEndpointService } from 'src/app/clearance/clearance.service';
import { Shipper } from 'src/app/clearance/models/shipper.model';
import { Customer } from 'src/app/customer/customer.model';
import { CustomerService } from 'src/app/customer/customer.service';
import { Driver } from 'src/app/fleet/driver/driver.model';
import { DriverService } from 'src/app/fleet/driver/driver.service';
import { Location } from 'src/app/location/location.model';
import { LocationService } from 'src/app/location/location.service';
import { City } from 'src/app/models/common/city.model';
import { Country } from 'src/app/models/common/country.model';
import { LatLng } from 'src/app/models/common/lat-lng';
import { PartyService } from 'src/app/party/party.service';
import { PartyType } from 'src/app/party/party-type.enum';
import { BaseMasterDataService } from 'src/app/services/base-master-data.service';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { LocationSearchComponent } from '../location-search/location-search.component';
import { countryPhoneValidator, normalizePhoneForCountry, phonePlaceholderForCountry } from '../utils/country-phone.util';
import { LaborerService } from '../../field-work/laborer.service';
import { Laborer } from '../../field-work/laborer.model';

export type PartyQuickCreateMode =
    'company'
    | 'customer'
    | 'employee'
    | 'broker'
    | 'brokerContact'
    | 'shipper'
    | 'location'
    | 'transportationAgency'
    | 'driver'
    | 'laborer'
    | 'customsAgent';
export type PartyQuickCreateType =
    | PartyQuickCreateMode
    | 'shipperContact'
    | 'consignee'
    | 'consigneeContact';

export interface PartyQuickCreateResult<T = unknown> {
    action: 'created' | 'updated';
    mode: PartyQuickCreateMode;
    type: PartyQuickCreateType;
    record: T;
}

@Component({
    selector: 'app-party-quick-create-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        DialogModule,
        ButtonModule,
        ConfirmDialogModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        SelectModule,
        LocationSearchComponent
    ],
    templateUrl: './party-quick-create-dialog.component.html',
    styleUrls: ['./party-quick-create-dialog.component.css'],
    providers: [BaseMasterDataService]
})
export class PartyQuickCreateDialogComponent implements OnChanges {
    private readonly defaultEmployeeRoleName = 'Operations Executive';
    private readonly saveTimeoutMs = 30000;
    private readonly employeeRoleLookupTimeoutMs = 10000;

    @Input() visible = false;
    @Input() mode: PartyQuickCreateMode | null = null;
    @Input() type: PartyQuickCreateType | null = null;
    @Input() countries: Country[] = [];
    @Input() owningCompany: Company | null = null;
    @Input() linkedCompany: Company | null = null;
    @Input() contactCompany: Company | null = null;
    @Input() lockContactCompany = false;
    @Input() defaultCountryId: number | null = null;
    @Input() record: Company | Customer | FreightForwarder | Shipper | Location | TransportationAgency | Driver | Laborer | null = null;
    @Input() companyRequired = true;
    @Input() submitLabel: string | null = null;
    @Input() titleOverride: string | null = null;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() created = new EventEmitter<PartyQuickCreateResult>();
    @Output() failed = new EventEmitter<string>();

    form: FormGroup;
    cities: City[] = [];
    companyOptions: Company[] = [];
    saving = false;
    errorMessage = '';
    phonePlaceholder = 'Phone number';

    constructor(
        private fb: FormBuilder,
        private freightForwarderService: FreightForwarderService,
        private transportationAgencyService: TransportationAgencyService,
        private clearanceService: CustomsClearanceEndpointService,
        private companyService: CompanyService,
        private customerService: CustomerService,
        private driverService: DriverService,
        private locationService: LocationService,
        private partyService: PartyService,
        private laborerService: LaborerService,
        private baseMasterDataService: BaseMasterDataService,
        private sessionStorageService: SessionStorageService,
        private confirmationService: ConfirmationService
    ) {
        this.form = this.fb.group({});
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasBuiltControls = Object.keys(this.form.controls).length > 0;
        const becameVisible = changes['visible']?.currentValue === true;
        const modeChanged = !!changes['mode'] || !!changes['type'];
        const editContextChanged = !!changes['record'] || !!changes['linkedCompany'] || !!changes['contactCompany'] || !!changes['lockContactCompany'];
        const shouldRebuild =
            !!this.resolvedMode
            && (
                !hasBuiltControls
                || becameVisible
                || modeChanged
                || (this.visible && editContextChanged)
            );

        if (shouldRebuild) {
            this.buildForm();
        }
    }

    get resolvedMode(): PartyQuickCreateMode | null {
        if (this.mode) return this.mode;
        if (this.type === 'consignee') return 'company';
        if (this.type === 'shipperContact' || this.type === 'consigneeContact') return 'customer';
        return this.type as PartyQuickCreateMode | null;
    }

    get resolvedType(): PartyQuickCreateType | null {
        return this.type ?? this.mode;
    }

    get isEditing(): boolean {
        return !!this.recordId();
    }

    get isCustomerMode(): boolean {
        return this.resolvedMode === 'customer';
    }

    get isEmployeeMode(): boolean {
        return this.resolvedMode === 'employee';
    }

    get title(): string {
        if (this.titleOverride?.trim()) return this.titleOverride.trim();
        const mode = this.resolvedMode;
        const action = this.isEditing ? 'Edit' : 'Create';
        if (mode === 'broker') return `${action} Customs Broker`;
        if (mode === 'brokerContact') return `${action} Broker Contact`;
        if (mode === 'shipper') return `${action} Shipper`;
        if (mode === 'transportationAgency') return `${action} Transportation Agency`;
        if (mode === 'driver') return `${action} Driver`;
        if (mode === 'company') return `${action} Company`;
        if (mode === 'employee') return `${action} Employee`;
        if (mode === 'laborer') return `${action} Laborer`;
        if (mode === 'customsAgent') return `${action} Customs Agent`;
        if (mode === 'location') return `${action} Delivery Location`;
        if (mode === 'customer') return `${action} Customer Contact`;
        return action;
    }

    get submitActionLabel(): string {
        if (this.submitLabel?.trim()) return this.submitLabel.trim();
        return this.isEditing ? 'Update' : 'Create';
    }

    get summaryEyebrow(): string {
        const mode = this.resolvedMode;
        if (mode === 'broker') return 'Customs broker';
        if (mode === 'brokerContact') return 'Broker contact';
        if (mode === 'shipper') return 'Shipment party';
        if (mode === 'transportationAgency') return 'Fleet agency';
        if (mode === 'driver') return 'Fleet driver';
        if (mode === 'company') return 'Company master';
        if (mode === 'employee') return 'Internal user';
        if (mode === 'laborer') return 'Field labor';
        if (mode === 'customsAgent') return 'Clearance contact';
        if (mode === 'location') return 'Managed location';
        return 'Customer entity';
    }

    get summaryTitle(): string {
        return this.form.get('name')?.value || this.title.replace(/^Create |^Edit /, '');
    }

    get summaryText(): string {
        const mode = this.resolvedMode;
        if (mode === 'broker') return 'Creates or updates a freight forwarder and keeps it available for customs clearance workflows.';
        if (mode === 'brokerContact') return 'Creates or updates a broker contact under the selected company and links it with the broker party classification.';
        if (mode === 'shipper') return 'Creates or updates a shipper master owned by the current company.';
        if (mode === 'transportationAgency') return 'Creates or updates a transportation agency master and links it to the current tenant.';
        if (mode === 'driver') return 'Creates or updates a fleet driver with the minimum compliance and transportation-agency details required by operations.';
        if (mode === 'company') return 'Creates or updates a company record and links it to the current tenant for partner selection.';
        if (mode === 'employee') return 'Creates or updates an employee contact and manages default role plus password setup on creation.';
        if (mode === 'laborer') return 'Creates or updates a laborer contact under the selected company and links it with the laborer party classification.';
        if (mode === 'customsAgent') return 'Creates or updates a customs clearance contact under the selected company and links it with the customs agent party classification.';
        if (mode === 'location') return 'Creates or updates a delivery location managed by the current company.';
        return `Creates or updates a customer record under ${this.effectiveContactCompany()?.name || 'the selected company'}.`;
    }

    get primaryFactLabel(): string {
        if (this.resolvedMode === 'location') return 'Managed by';
        if (this.resolvedMode === 'driver') return 'Agency';
        if (this.isCustomerLike()) return 'Customer under';
        return 'Tenant';
    }

    get primaryFactValue(): string {
        if (this.resolvedMode === 'driver') {
            return this.transportationAgencyName() || '-';
        }
        if (this.isCustomerLike()) {
            return this.effectiveContactCompany()?.name || '-';
        }
        return this.owningCompany?.name || '-';
    }

    get secondaryFactLabel(): string {
        if (this.resolvedMode === 'shipper') return 'Entity type';
        if (this.resolvedMode === 'transportationAgency') return 'Entity type';
        if (this.resolvedMode === 'driver') return 'Compliance';
        if (this.resolvedMode === 'company') return 'Places autofill';
        if (this.resolvedMode === 'location') return 'City required';
        if (this.resolvedMode === 'employee') return 'Default role';
        return 'Linking';
    }

    get secondaryFactValue(): string {
        if (this.resolvedMode === 'shipper') return 'Shipper';
        if (this.resolvedMode === 'transportationAgency') return 'Transportation agency';
        if (this.resolvedMode === 'driver') return 'License + residence';
        if (this.resolvedMode === 'company') return 'Enabled';
        if (this.resolvedMode === 'location') return 'Yes';
        if (this.resolvedMode === 'employee') return this.defaultEmployeeRoleName;
        if (this.resolvedMode === 'laborer') return 'Laborer';
        if (this.resolvedMode === 'customsAgent') return 'Customs agent';
        return this.isCustomerLike() ? 'Customer' : 'Tenant party';
    }

    get showAddressSearch(): boolean {
        return this.resolvedMode !== null;
    }

    get showCountryField(): boolean {
        return this.resolvedMode === 'broker'
            || this.resolvedMode === 'company'
            || this.resolvedMode === 'customer'
            || this.resolvedMode === 'location'
            || this.resolvedMode === 'transportationAgency'
            || this.resolvedMode === 'driver';
    }

    get showCompanySelector(): boolean {
        if (this.resolvedMode === 'customer') {
            return !this.lockContactCompany;
        }
        return (this.resolvedMode === 'laborer'
            || this.resolvedMode === 'brokerContact'
            || this.resolvedMode === 'customsAgent')
            && this.companyRequired
            && !this.linkedCompany
            && !(this.record as Customer | null)?.company?.id;
    }

    close(): void {
        this.errorMessage = '';
        this.visible = false;
        this.visibleChange.emit(false);
    }

    onCountryChange(countryId: number | null, resetCity = true, preferredCityId?: number | null): void {
        if (resetCity) {
            this.form.get('cityId')?.patchValue(null, { emitEvent: false });
        }
        this.cities = [];
        if (!countryId) return;
        this.baseMasterDataService.getCities('en-US', Number(countryId)).subscribe(cities => {
            this.cities = cities ?? [];
            if (preferredCityId && !this.form.get('cityId')?.value) {
                const selectedCity = this.cities.find(city => city.id === preferredCityId);
                if (selectedCity) {
                    this.form.patchValue({ cityId: selectedCity.id }, { emitEvent: false });
                }
            }
            this.applyPhoneValidator();
        });
    }

    onCountryFieldChange(countryId: number | null): void {
        if (this.resolvedMode === 'location' || this.resolvedMode === 'driver') {
            this.onCountryChange(countryId);
            return;
        }
        this.applyPhoneValidator();
    }

    save(): void {
        if (this.saving) {
            return;
        }

        this.errorMessage = '';
        if (!this.resolvedMode || this.form.invalid || !this.owningCompany?.id) {
            this.form.markAllAsTouched();
            this.errorMessage = 'Please complete the required fields before saving.';
            return;
        }

        this.executeSave(this.resolvedMode);
    }

    private executeSave(mode: PartyQuickCreateMode): void {
        this.saving = true;
        this.saveRequest(mode).pipe(
            timeout({ each: this.saveTimeoutMs }),
            finalize(() => this.saving = false)
        ).subscribe({
            next: record => {
                this.errorMessage = '';
                this.created.emit({
                    action: this.isEditing ? 'updated' : 'created',
                    mode,
                    type: this.resolvedType || mode,
                    record
                });
                this.close();
            },
            error: error => {
                const collision = this.extractIdentityCollision(error);
                if (collision) {
                    this.errorMessage = '';
                    this.presentIdentityCollision(collision, mode);
                    return;
                }
                const message = error?.name === 'TimeoutError'
                    ? `The ${this.errorLabel()} request timed out. Check the employee list before retrying; the server may have completed it.`
                    : error?.error?.message || error?.message || `Unable to save ${this.errorLabel()}.`;
                this.errorMessage = message;
                this.failed.emit(message);
            }
        });
    }

    buildForm(): void {
        this.cities = [];
        const mode = this.resolvedMode;
        const countryId = this.recordCountryId() ?? this.defaultCountryId ?? this.owningCompany?.countryId ?? this.countries?.[0]?.id ?? null;
        const address = this.recordAddress();
        const company = this.recordCompany() ?? this.linkedCompany ?? this.contactCompany ?? null;

        if (mode === 'broker') {
            const broker = this.record as FreightForwarder | null;
            this.form = this.fb.group({
                name: [broker?.name || '', Validators.required],
                countryId: [countryId, Validators.required],
                crNumber: [broker?.crNumber ?? null],
                phoneNumber: [broker?.phoneNumber || ''],
                emailAddress: [broker?.emailAddress || '', Validators.email],
                address: [address]
            });
            return;
        }

        if (mode === 'shipper') {
            const shipper = this.record as Shipper | null;
            this.form = this.fb.group({
                name: [shipper?.shipperName || '', Validators.required],
                phoneNumber: [shipper?.contactPhone || ''],
                emailAddress: [shipper?.contactEmail || '', Validators.email],
                address: [address]
            });
            return;
        }

        if (mode === 'transportationAgency') {
            const agency = this.record as TransportationAgency | null;
            this.form = this.fb.group({
                name: [agency?.agencyName || '', Validators.required],
                countryId: [countryId, Validators.required],
                crNumber: [agency?.crNumber ?? null, Validators.required],
                motAgencyLicenseNumber: [agency?.motAgencyLicenseNumber || ''],
                phoneNumber: [agency?.phoneNumber || '', Validators.required],
                emailAddress: [agency?.emailAddress || '', Validators.email],
                websiteURL: [agency?.website || ''],
                address: [address]
            });
            return;
        }

        if (mode === 'company') {
            const companyRecord = this.record as Company | null;
            this.form = this.fb.group({
                name: [companyRecord?.name || '', Validators.required],
                countryId: [countryId, Validators.required],
                crNumber: [companyRecord?.crNumber ?? null, Validators.required],
                taxRegNumber: [companyRecord?.taxRegNumber || ''],
                phoneNumber: [companyRecord?.phoneNumber || '', Validators.required],
                emailAddress: [companyRecord?.emailAddress || '', Validators.email],
                websiteURL: [companyRecord?.websiteURL || ''],
                address: [address]
            });
            return;
        }

        if (mode === 'location') {
            const location = this.record as Location | null;
            this.form = this.fb.group({
                name: [location?.name || '', Validators.required],
                countryId: [countryId, Validators.required],
                cityId: [location?.cityId ?? null, Validators.required],
                phoneNumber: [location?.telephoneNumber || ''],
                address: [address]
            });
            if (countryId) {
                this.onCountryChange(countryId, false, location?.cityId ?? null);
            }
            return;
        }

        if (mode === 'driver') {
            const driver = this.record as Driver | null;
            this.form = this.fb.group({
                name: [driver?.name || '', Validators.required],
                phoneNumber: [driver?.phoneNumber || '', Validators.required],
                emailAddress: [driver?.emailAddress || '', Validators.email],
                address: [driver?.preferredAddress || address],
                countryId: [countryId, Validators.required],
                cityId: [driver?.residingCity?.id ?? null, Validators.required],
                residenceNumber: [driver?.residenceNumber || '', Validators.required],
                residenceExpiryDate: [driver?.residenceExpiryDate || null],
                licenseNumber: [driver?.licenseNumber || ''],
                licenseExpiryDate: [driver?.licenseExpiryDate || null]
            });
            this.onCountryChange(countryId, false, driver?.residingCity?.id ?? null);
            this.applyPhoneValidator();
            return;
        }

        if (mode === 'customer' || mode === 'employee' || mode === 'laborer' || mode === 'brokerContact' || mode === 'customsAgent') {
            const customer = this.record as Customer | null;
            const controls: Record<string, any> = {
                name: [customer?.name || '', Validators.required],
                emailAddress: [customer?.emailAddress || '', Validators.email],
                phoneNumber: [customer?.phoneNumber || '', mode === 'customer' ? [] : Validators.required],
                corporateRole: [customer?.corporateRole || ''],
                companyId: [company, mode === 'customer' ? [] : this.showCompanySelector ? Validators.required : []]
            };
            if (mode === 'customer') {
                controls['countryId'] = [countryId, Validators.required];
                controls['address'] = [address];
                this.form = this.fb.group(controls, { validators: this.emailOrPhoneValidator() });
            } else {
                controls['address'] = [address];
                this.form = this.fb.group(controls);
            }
            if (this.showCompanySelector) {
                this.loadCompanies();
            } else {
                this.companyOptions = company ? [company] : [];
            }
            return;
        }

        this.form = this.fb.group({});
    }

    placeSelected(place: google.maps.places.PlaceResult): void {
        if (!place) return;
        const countryId = this.resolveCountryIdFromPlace(place);
        const address = place.formatted_address || '';
        const phoneNumber = this.cleanPhoneNumber(place.international_phone_number || place.formatted_phone_number || '');
        const placeName = place.name?.trim() || '';
        const website = place.website?.trim() || '';
        const logo = place.photos?.[0]?.getUrl?.() || null;
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();

        this.form.patchValue({ address }, { emitEvent: false });

        if (this.resolvedMode === 'company') {
            this.patchIfEmpty('name', placeName);
            this.patchIfEmpty('phoneNumber', phoneNumber);
            this.patchIfEmpty('websiteURL', website);
        } else if (this.resolvedMode === 'transportationAgency') {
            this.patchIfEmpty('name', placeName);
            this.patchIfEmpty('phoneNumber', phoneNumber);
            this.patchIfEmpty('websiteURL', website);
        } else if (this.resolvedMode === 'broker') {
            this.patchIfEmpty('name', placeName);
            this.patchIfEmpty('phoneNumber', phoneNumber);
        } else if (this.resolvedMode === 'shipper') {
            this.patchIfEmpty('name', placeName);
            this.patchIfEmpty('phoneNumber', phoneNumber);
        } else if (this.resolvedMode === 'customer' || this.resolvedMode === 'employee' || this.resolvedMode === 'laborer' || this.resolvedMode === 'customsAgent') {
            this.patchIfEmpty('phoneNumber', phoneNumber);
        } else if (this.resolvedMode === 'driver') {
            this.patchIfEmpty('phoneNumber', phoneNumber);
        } else if (this.resolvedMode === 'location') {
            this.patchIfEmpty('name', placeName);
            this.patchIfEmpty('phoneNumber', phoneNumber);
        }

        if (countryId && !this.form.get('countryId')?.value && this.showCountryField) {
            this.form.patchValue({ countryId }, { emitEvent: false });
        }

        if ((this.resolvedMode === 'location' || this.resolvedMode === 'driver') && countryId) {
            this.baseMasterDataService.getCities('en-US', Number(countryId)).subscribe(cities => {
                this.cities = cities ?? [];
                const preferredCityId = this.resolveCityIdFromCandidates(place, this.cities);
                this.form.patchValue({
                    countryId,
                    cityId: this.form.get('cityId')?.value || preferredCityId || null
                }, { emitEvent: false });
                this.applyPhoneValidator();
            });
        }

        this.record = this.mergePlaceMetadata(this.record, {
            address,
            countryId,
            placeId: place.place_id || null,
            logo,
            location: lat != null && lng != null ? new LatLng(lat, lng) : null,
            phoneNumber,
            websiteURL: website
        });
    }

    private saveRequest(mode: PartyQuickCreateMode): Observable<unknown> {
        if (mode === 'laborer') {
            const payload = this.toLaborerPayload();
            return this.laborerService.save(payload);
        }
        if (mode === 'broker') {
            const payload = this.toBrokerPayload();
            return (payload.id
                ? this.freightForwarderService.updateFreightForwarder(payload)
                : this.freightForwarderService.addFreightForwarder(payload).pipe(
                    switchMap(created => this.linkParty(created.id, PartyType.FreightForwarder).pipe(map(() => created)))
                ));
        }

        if (mode === 'shipper') {
            const payload = this.toShipperPayload();
            return (payload.shipperID
                ? this.clearanceService.updateShipper(payload.shipperID, payload)
                : this.clearanceService.createShipper(payload).pipe(
                    switchMap(created => this.linkParty(created.shipperID!, PartyType.Shipper).pipe(map(() => created)))
                ));
        }

        if (mode === 'transportationAgency') {
            const payload = this.toTransportationAgencyPayload();
            return (payload.rowId
                ? this.transportationAgencyService.updateTransportationAgency(payload).pipe(
                    map(response => this.requireWrappedData(response, 'Unable to update transportation agency.'))
                )
                : this.transportationAgencyService.addTransportationAgency(payload).pipe(
                    switchMap(response => {
                        const created = this.requireWrappedData(response, 'Unable to create transportation agency.');
                        return this.linkParty(created.rowId, PartyType.TransportationAgency).pipe(map(() => created));
                    })
                ));
        }

        if (mode === 'company') {
            const payload = this.toCompanyPayload();
            return (payload.id
                ? this.companyService.updateCompany(payload).pipe(
                    map(response => this.requireWrappedData(response, 'Unable to update company.'))
                )
                : this.companyService.addCompany(payload).pipe(
                    switchMap(response => {
                        const created = this.requireWrappedData(response, 'Unable to create company.');
                        return this.linkParty(created.id, PartyType.Company).pipe(map(() => created));
                    })
                ));
        }

        if (mode === 'location') {
            const payload = this.toLocationPayload();
            return (payload.id
                ? this.locationService.updateLocation(payload).pipe(map(response => response.data))
                : this.locationService.createLocation(payload).pipe(map(response => response.data)));
        }

        if (mode === 'driver') {
            const payload = this.toDriverPayload();
            return (payload.id
                ? this.driverService.updateDriver(payload).pipe(
                    map(response => this.requireWrappedData(response, 'Unable to update driver.'))
                )
                : this.driverService.addDriver(payload).pipe(
                    switchMap(response => {
                        const created = this.requireWrappedData(response, 'Unable to create driver.');
                        return this.linkParty(created.id, PartyType.Driver).pipe(map(() => created));
                    })
                ));
        }

        const customerPayload = this.toCustomerPayload(mode);
        if (mode === 'employee') {
            if (customerPayload.id) {
                // Employee invitations already create the HR employee and its tenant link.
                // Updating the customer is all that is required here; calling the generic
                // party-link endpoint with a customer id can leave the dialog waiting forever.
                return this.customerService.updateCustomer(customerPayload).pipe(
                    map(response => this.customerFromResponse(response) || customerPayload)
                );
            }

            // A timed-out invitation may have completed on the server. Check by identity
            // before allowing another invitation, which would create a duplicate employee.
            return this.findExistingCustomerForCreate(customerPayload).pipe(
                switchMap(existing => existing
                    ? throwError(() => new Error(this.employeeDuplicateMessage(existing, customerPayload)))
                    : this.inviteEmployee(customerPayload)
                )
            );
        }

        if (customerPayload.id) {
            return this.customerService.updateCustomer(customerPayload).pipe(
                map(response => this.customerFromResponse(response) || customerPayload),
                switchMap(saved => this.ensureCustomerPartyLink(saved, mode, customerPayload))
            );
        }

        return this.findExistingCustomerForCreate(customerPayload).pipe(
            switchMap(existing => {
                if (existing) {
                    return this.ensureCustomerPartyLink(existing, mode, customerPayload);
                }
                const createRequest = mode === 'customer'
                    ? this.customerService.createCustomerContact(customerPayload)
                    : this.customerService.createCustomer(customerPayload);
                return createRequest.pipe(
                    switchMap(response => {
                        const created = this.customerFromResponse(response);
                        if (!created) {
                            return throwError(() => new Error(this.responseMessage(
                                response,
                                'Customer contact could not be created. Verify the contact details and try again.'
                            )));
                        }

                        return this.ensureCustomerPartyLink(created, mode, customerPayload);
                    })
                );
            })
        );
    }

    private customerFromResponse(response: unknown): Customer | null {
        const wrapped = response as { data?: Customer | null };
        if (wrapped?.data) return wrapped.data;

        const direct = response as Customer | null;
        return direct && (direct.id || direct.name || direct.emailAddress || direct.phoneNumber) ? direct : null;
    }

    private responseMessage(response: unknown, fallbackMessage: string): string {
        const wrapped = response as { message?: string | null } | null;
        return wrapped?.message?.trim() || fallbackMessage;
    }

    private requireWrappedData<T>(response: { data?: T | null; message?: string | null }, fallbackMessage: string): T {
        if (response?.data) {
            return response.data;
        }
        throw new Error(response?.message || fallbackMessage);
    }

    private ensureCustomerPartyLink(created: Customer, mode: PartyQuickCreateMode, customerPayload: Customer): Observable<Customer> {
        if (!created?.id) {
            return throwError(() => new Error('Customer save response did not include an id.'));
        }
        return this.linkParty(
            created.id,
            this.partyTypeForCustomerMode(mode)
        ).pipe(map(() => created));
    }

    private findExistingCustomerForCreate(customerPayload: Customer): Observable<Customer | null> {
        const emailAddress = `${customerPayload.emailAddress || ''}`.trim();
        const phoneNumber = `${customerPayload.phoneNumber || ''}`.trim();

        if (emailAddress && !this.isSyntheticCustomerEmail(emailAddress, phoneNumber)) {
            return this.customerService.getCustomerByEmailAddress(emailAddress).pipe(
                map(response => response?.data || null),
                switchMap(existing => existing ? of(existing) : this.findExistingCustomerByPhone(phoneNumber)),
                catchError(() => this.findExistingCustomerByPhone(phoneNumber))
            );
        }

        return this.findExistingCustomerByPhone(phoneNumber);
    }

    private findExistingCustomerByPhone(phoneNumber: string): Observable<Customer | null> {
        if (!phoneNumber) {
            return of(null);
        }
        return this.customerService.getCustomerByMobileNumber(phoneNumber).pipe(
            map(response => response?.data || null),
            catchError(() => of(null))
        );
    }

    private isSyntheticCustomerEmail(emailAddress: string, phoneNumber: string): boolean {
        if (!emailAddress || !phoneNumber) {
            return false;
        }
        return emailAddress === `${phoneNumber.replace(/\+/g, '')}@abcxyz.com`;
    }

    private toCompanyPayload(): Company {
        const value = this.form.getRawValue();
        const current = (this.record as Company | null) ?? new Company();
        return {
            ...current,
            name: value.name?.trim(),
            crNumber: value.crNumber,
            taxRegNumber: value.taxRegNumber?.toString()?.trim() || null,
            emailAddress: value.emailAddress?.trim() || null,
            phoneNumber: value.phoneNumber?.trim() || null,
            websiteURL: value.websiteURL?.trim() || current.websiteURL || null,
            address: value.address?.trim() || '',
            logo: current.logo || null,
            placeId: current.placeId || null,
            location: current.location,
            countryId: value.countryId || this.defaultCountryId || this.owningCompany?.countryId || 0
        } as Company;
    }

    private toCustomerPayload(mode: PartyQuickCreateMode): Customer {
        const value = this.form.getRawValue();
        const current = (this.record as Customer | null) ?? new Customer();
        const selectedCompany = mode === 'employee'
            ? this.employeeCompany()
            : this.showCompanySelector
                ? value.companyId || this.effectiveContactCompany()
                : this.effectiveContactCompany();
        const emailAddress = value.emailAddress?.trim()
            || (mode !== 'customer' && value.phoneNumber ? value.phoneNumber.replace(/\+/g, '') + '@abcxyz.com' : null);

        return {
            ...current,
            name: value.name?.trim(),
            emailAddress,
            phoneNumber: value.phoneNumber?.trim(),
            corporateRole: value.corporateRole?.trim() || current.corporateRole,
            address: value.address?.trim() || '',
            company: mode === 'customer'
                ? selectedCompany || undefined as any
                : this.companyRequired || mode === 'employee' ? selectedCompany || undefined as any : undefined as any,
            countryId: value.countryId ?? selectedCompany?.countryId ?? this.defaultCountryId ?? this.owningCompany?.countryId ?? 0
        } as Customer;
    }

    private toLaborerPayload(): Laborer {
        const value = this.form.getRawValue();
        const current = (this.record as Laborer | null) ?? {};
        return {
            ...current,
            name: value.name?.trim(),
            emailAddress: value.emailAddress?.trim() || undefined,
            phoneNumber: value.phoneNumber?.trim() || undefined,
            address: value.address?.trim() || undefined,
            status: current.status || 'ACTIVE'
        };
    }

    private toBrokerPayload(): FreightForwarder {
        const value = this.form.getRawValue();
        const current = (this.record as FreightForwarder | null) ?? new FreightForwarder();
        return {
            ...current,
            name: value.name?.trim(),
            countryId: value.countryId || this.defaultCountryId || this.owningCompany?.countryId || 0,
            crNumber: value.crNumber,
            phoneNumber: value.phoneNumber?.trim() || current.phoneNumber,
            emailAddress: value.emailAddress?.trim() || current.emailAddress,
            address: value.address?.trim() || '',
            placeId: current.placeId,
            logo: current.logo,
            latitude: current.latitude,
            longitude: current.longitude
        } as FreightForwarder;
    }

    private toTransportationAgencyPayload(): TransportationAgency {
        const value = this.form.getRawValue();
        const current = (this.record as TransportationAgency | null) ?? new TransportationAgency();
        const country = this.countries.find(item => item.id === value.countryId) || current.country;
        return {
            ...current,
            agencyName: value.name?.trim(),
            crNumber: value.crNumber,
            motAgencyLicenseNumber: value.motAgencyLicenseNumber?.trim() || current.motAgencyLicenseNumber,
            phoneNumber: value.phoneNumber?.trim() || current.phoneNumber,
            emailAddress: value.emailAddress?.trim() || current.emailAddress,
            website: value.websiteURL?.trim() || current.website,
            address: value.address?.trim() || '',
            placeId: current.placeId,
            logo: current.logo,
            location: current.location,
            country
        } as TransportationAgency;
    }

    private toDriverPayload(): Driver {
        const value = this.form.getRawValue();
        const current = (this.record as Driver | null) ?? new Driver();
        const driverCountry = this.countries.find(country => country.id === value.countryId);
        const residingCity = this.cities.find(city => city.id === value.cityId)
            ?? (value.cityId ? { id: value.cityId } as City : undefined);
        return {
            ...current,
            name: value.name?.trim(),
            legalName: value.name?.trim(),
            phoneNumber: normalizePhoneForCountry(value.phoneNumber, driverCountry) || value.phoneNumber?.trim(),
            emailAddress: value.emailAddress?.trim() || current.emailAddress,
            preferredAddress: residingCity?.cityName || value.address?.trim(),
            preferredLocation: residingCity?.coordinates || current.preferredLocation,
            licenseNumber: value.licenseNumber?.trim(),
            licenseExpiryDate: value.licenseExpiryDate,
            residenceNumber: value.residenceNumber?.trim(),
            residenceExpiryDate: value.residenceExpiryDate,
            residingCity,
            nationality: current.nationality || driverCountry,
            residingCountry: current.residingCountry || driverCountry,
            currentTransportationAgency: current.currentTransportationAgency || this.resolvedTransportationAgency(),
            legalAgreementAccepted: current.legalAgreementAccepted ?? true
        } as Driver;
    }

    private toShipperPayload(): Shipper {
        const value = this.form.getRawValue();
        const current = (this.record as Shipper | null) ?? new Shipper(value.name?.trim(), this.owningCompany!.id);
        return {
            shipperID: current.shipperID,
            shipperName: value.name?.trim(),
            address: value.address?.trim() || '',
            contactPerson: current.contactPerson,
            contactPhone: value.phoneNumber?.trim() || current.contactPhone,
            contactEmail: value.emailAddress?.trim() || current.contactEmail,
            relatedCompanyId: current.relatedCompanyId || this.owningCompany!.id
        };
    }

    private toLocationPayload(): Location {
        const value = this.form.getRawValue();
        const current = (this.record as Location | null) ?? new Location();
        return {
            ...current,
            name: value.name?.trim(),
            countryId: value.countryId || this.defaultCountryId || this.owningCompany?.countryId || 0,
            cityId: value.cityId,
            telephoneNumber: value.phoneNumber?.trim() || null,
            shortDescription: value.address?.trim() || '',
            managedBy: this.owningCompany!,
            company: current.company || this.owningCompany!,
            contacts: current.contacts || [],
            merchandisers: current.merchandisers || []
        } as Location;
    }

    private loadCompanies(): void {
        if (!this.owningCompany?.id) {
            this.companyOptions = [];
            return;
        }
        this.partyService.getParties(this.owningCompany.id, ['Company'], '', 0, 1000).subscribe({
            next: response => {
                const fallbackCompany = this.effectiveContactCompany();
                const companies = (response.content || []).map(party => this.partyService.convertPartyToCompany(party));
                this.companyOptions = fallbackCompany && !companies.some(company => company.id === fallbackCompany.id)
                    ? [fallbackCompany, ...companies]
                    : companies;
                const selectedCompany = this.form.get('companyId')?.value;
                if (selectedCompany?.id) {
                    const match = this.companyOptions.find(company => company.id === selectedCompany.id);
                    if (match) {
                        this.form.patchValue({ companyId: match }, { emitEvent: false });
                    }
                }
            },
            error: () => {
                this.companyOptions = this.effectiveContactCompany() ? [this.effectiveContactCompany()!] : [];
            }
        });
    }

    private linkParty(linkedPartyId: number, partyType: PartyType): Observable<unknown> {
        return this.partyService.linkPartyToCompany(linkedPartyId, this.owningCompany!.id, partyType);
    }

    private effectiveContactCompany(): Company | null {
        const customerRecord = this.record as Customer | null;
        return this.linkedCompany
            ?? this.contactCompany
            ?? customerRecord?.company
            ?? (this.isEmployeeMode ? this.employeeCompany() : null)
            ?? (this.isCustomerMode ? null : this.companyRequired ? this.owningCompany : null);
    }

    private employeeCompany(): Company | null {
        return this.linkedCompany ?? this.contactCompany ?? this.owningCompany;
    }

    private patchIfEmpty(controlName: string, value: string | number | null | undefined): void {
        if (!value && value !== 0) return;
        const control = this.form.get(controlName);
        if (!control) return;
        const current = control.value;
        if (current !== null && current !== undefined && `${current}`.trim() !== '') return;
        control.patchValue(value, { emitEvent: false });
    }

    private emailOrPhoneValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const phone = `${control.get('phoneNumber')?.value || ''}`.trim();
            const email = `${control.get('emailAddress')?.value || ''}`.trim();
            return phone || email ? null : { emailOrPhoneRequired: true };
        };
    }

    private mergePlaceMetadata(
        currentRecord: Company | Customer | FreightForwarder | Shipper | Location | TransportationAgency | Driver | Laborer | null,
        placeData: {
            address: string;
            countryId: number | null;
            placeId: string | null;
            logo: string | null;
            location: LatLng | null;
            phoneNumber: string | null;
            websiteURL: string | null;
        }
    ): Company | Customer | FreightForwarder | Shipper | Location | TransportationAgency | Driver | Laborer | null {
        if (this.resolvedMode === 'company') {
            return {
                ...(currentRecord as Company | null ?? new Company()),
                address: placeData.address,
                countryId: placeData.countryId ?? (currentRecord as Company | null)?.countryId,
                placeId: placeData.placeId || (currentRecord as Company | null)?.placeId,
                logo: (currentRecord as Company | null)?.logo || placeData.logo,
                location: placeData.location ?? (currentRecord as Company | null)?.location,
                phoneNumber: (currentRecord as Company | null)?.phoneNumber || placeData.phoneNumber,
                websiteURL: (currentRecord as Company | null)?.websiteURL || placeData.websiteURL
            } as Company;
        }
        if (this.resolvedMode === 'transportationAgency') {
            return {
                ...(currentRecord as TransportationAgency | null ?? new TransportationAgency()),
                address: placeData.address,
                placeId: placeData.placeId || (currentRecord as TransportationAgency | null)?.placeId,
                logo: (currentRecord as TransportationAgency | null)?.logo || placeData.logo,
                location: placeData.location ?? (currentRecord as TransportationAgency | null)?.location,
                phoneNumber: (currentRecord as TransportationAgency | null)?.phoneNumber || placeData.phoneNumber,
                website: (currentRecord as TransportationAgency | null)?.website || placeData.websiteURL
            } as TransportationAgency;
        }
        if (this.resolvedMode === 'broker') {
            return {
                ...(currentRecord as FreightForwarder | null ?? new FreightForwarder()),
                address: placeData.address,
                countryId: placeData.countryId ?? (currentRecord as FreightForwarder | null)?.countryId,
                placeId: placeData.placeId || (currentRecord as FreightForwarder | null)?.placeId,
                logo: (currentRecord as FreightForwarder | null)?.logo || placeData.logo,
                latitude: (currentRecord as FreightForwarder | null)?.latitude ?? placeData.location?.latitude ?? 0,
                longitude: (currentRecord as FreightForwarder | null)?.longitude ?? placeData.location?.longitude ?? 0,
                phoneNumber: (currentRecord as FreightForwarder | null)?.phoneNumber || placeData.phoneNumber
            } as FreightForwarder;
        }
        if (this.resolvedMode === 'location') {
            return {
                ...(currentRecord as Location | null ?? new Location()),
                shortDescription: placeData.address,
                countryId: placeData.countryId ?? (currentRecord as Location | null)?.countryId,
                placeId: placeData.placeId || (currentRecord as Location | null)?.placeId,
                location: placeData.location ?? (currentRecord as Location | null)?.location
            } as Location;
        }
        if (this.resolvedMode === 'laborer') {
            return {
                ...(currentRecord as Laborer | null ?? {}),
                address: placeData.address,
                phoneNumber: (currentRecord as Laborer | null)?.phoneNumber || placeData.phoneNumber || undefined
            } as Laborer;
        }
        if (this.resolvedMode === 'customer' || this.resolvedMode === 'employee' || this.resolvedMode === 'brokerContact' || this.resolvedMode === 'customsAgent') {
            return {
                ...(currentRecord as Customer | null ?? new Customer()),
                address: placeData.address,
                phoneNumber: (currentRecord as Customer | null)?.phoneNumber || placeData.phoneNumber || undefined as any
            } as Customer;
        }
        if (this.resolvedMode === 'driver') {
            return {
                ...(currentRecord as Driver | null ?? new Driver()),
                preferredAddress: placeData.address,
                preferredLocation: (currentRecord as Driver | null)?.preferredLocation || placeData.location
            } as Driver;
        }
        if (this.resolvedMode === 'shipper') {
            return {
                ...(currentRecord as Shipper | null ?? new Shipper('', this.owningCompany?.id || 0)),
                address: placeData.address,
                contactPhone: (currentRecord as Shipper | null)?.contactPhone || placeData.phoneNumber || undefined
            } as Shipper;
        }
        return currentRecord;
    }

    private resolveCountryIdFromPlace(place: google.maps.places.PlaceResult): number | null {
        const countryCode = this.addressComponent(place, 'country')?.short_name;
        if (!countryCode) return null;
        return this.countries.find(country => country.countryCode === countryCode)?.id ?? null;
    }

    private resolveCityIdFromCandidates(place: google.maps.places.PlaceResult, cities: City[]): number | null {
        const cityCandidates = this.cityNameCandidatesFromPlace(place);
        const exactMatch = (cities || []).find(city =>
            cityCandidates.some(candidate => this.normalizedValue(candidate) === this.normalizedValue(city.cityName))
        );
        return exactMatch?.id ?? null;
    }

    private cityNameCandidatesFromPlace(place: google.maps.places.PlaceResult): string[] {
        return [
            this.addressComponent(place, 'locality')?.long_name,
            this.addressComponent(place, 'postal_town')?.long_name,
            this.addressComponent(place, 'administrative_area_level_2')?.long_name,
            this.addressComponent(place, 'sublocality')?.long_name,
            this.addressComponent(place, 'sublocality_level_1')?.long_name
        ].filter((value): value is string => !!value?.trim());
    }

    private addressComponent(place: google.maps.places.PlaceResult, type: string): google.maps.GeocoderAddressComponent | undefined {
        return place.address_components?.find(component => component.types.includes(type));
    }

    private normalizedValue(value?: string | null): string {
        return `${value || ''}`.trim().toLowerCase();
    }

    private cleanPhoneNumber(value?: string | null): string | null {
        const phone = `${value || ''}`.replace(/\s+/g, '').trim();
        return phone || null;
    }

    private recordId(): number | undefined {
        const current = this.record as any;
        return current?.id ?? current?.shipperID;
    }

    private recordAddress(): string {
        if (!this.record) return '';
        if (this.resolvedMode === 'location') {
            return (this.record as Location).shortDescription || '';
        }
        if (this.resolvedMode === 'shipper') {
            return (this.record as Shipper).address || '';
        }
        return (this.record as any).address || '';
    }

    private recordCountryId(): number | null {
        if (!this.record) return null;
        if (this.resolvedMode === 'transportationAgency') {
            return (this.record as TransportationAgency).country?.id ?? null;
        }
        if (this.resolvedMode === 'driver') {
            const driver = this.record as Driver;
            return driver.residingCountry?.id ?? driver.nationality?.id ?? null;
        }
        return (this.record as any).countryId ?? null;
    }

    private recordCompany(): Company | null {
        if (!['customer', 'employee', 'laborer', 'brokerContact', 'customsAgent'].includes(`${this.resolvedMode}`)) return null;
        return (this.record as Customer | null)?.company ?? null;
    }

    private partyTypeForCustomerMode(mode: PartyQuickCreateMode): PartyType {
        if (mode === 'employee') return PartyType.Employee;
        if (mode === 'laborer') return PartyType.Laborer;
        if (mode === 'brokerContact') return PartyType.Broker;
        if (mode === 'customsAgent') return PartyType.CustomsClearanceAgent;
        return PartyType.Customer;
    }

    private resolvedTransportationAgency(): TransportationAgency | undefined {
        if (this.record && this.resolvedMode === 'driver') {
            return (this.record as Driver).currentTransportationAgency
                || this.sessionStorageService.getTransportationAgency()
                || undefined;
        }
        return this.sessionStorageService.getTransportationAgency() || undefined;
    }

    private transportationAgencyName(): string | null {
        return this.resolvedTransportationAgency()?.agencyName || null;
    }

    private applyPhoneValidator(): void {
        if (this.resolvedMode !== 'driver') {
            return;
        }
        const country = this.countries.find(item => item.id === this.form.get('countryId')?.value) || null;
        this.phonePlaceholder = phonePlaceholderForCountry(country);
        const control = this.form.get('phoneNumber');
        control?.setValidators([Validators.required, countryPhoneValidator(() => country)]);
        control?.updateValueAndValidity({ emitEvent: false });
    }

    private inviteEmployee(customerPayload: Customer): Observable<Customer> {
        const companyId = this.employeeCompany()?.id;
        if (!companyId) {
            return throwError(() => new Error('Unable to resolve the owning company for this employee invitation.'));
        }

        return this.resolveDefaultEmployeeRoleIds().pipe(
            switchMap(roleIds => this.companyService.inviteEmployee(companyId, {
                name: customerPayload.name,
                emailAddress: customerPayload.emailAddress,
                phoneNumber: customerPayload.phoneNumber,
                corporateRole: customerPayload.corporateRole,
                address: customerPayload.address,
                countryId: customerPayload.countryId,
                roleIds
            })),
            map(response => {
                const invited = this.customerFromResponse(response);
                if (!invited) {
                    throw new Error('Employee invitation response did not include customer details.');
                }
                return invited;
            }),
            switchMap(invited => this.ensureCustomerPartyLink(invited, 'employee', customerPayload))
        );
    }

    private resolveDefaultEmployeeRoleIds(): Observable<number[]> {
        return this.customerService.getAllRoles().pipe(
            timeout({ each: this.employeeRoleLookupTimeoutMs }),
            map(roles => {
                const defaultRole = (roles ?? []).find(role => role.roleName === this.defaultEmployeeRoleName);
                return defaultRole?.roleId ? [defaultRole.roleId] : [];
            }),
            catchError(() => of([]))
        );
    }

    private employeeDuplicateMessage(existing: Customer, payload: Customer): string {
        const email = `${payload.emailAddress || ''}`.trim();
        const phone = `${payload.phoneNumber || ''}`.trim();
        const identity = email && !this.isSyntheticCustomerEmail(email, phone)
            ? `email ${email}`
            : phone
                ? `phone number ${phone}`
                : 'the supplied contact details';
        const existingLabel = existing.name ? ` (${existing.name}${existing.id ? `, ID ${existing.id}` : ''})` : '';
        return `An employee with ${identity} already exists${existingLabel}. Refresh the employee list before retrying.`;
    }

    private extractIdentityCollision(error: any): IdentityCollisionDetails | null {
        const details = error?.error?.details;
        if (error?.status !== 409 || !details || details.code !== 'IDENTITY_ALREADY_EXISTS') {
            return null;
        }
        return details as IdentityCollisionDetails;
    }

    private presentIdentityCollision(collision: IdentityCollisionDetails, mode: PartyQuickCreateMode): void {
        this.confirmationService.confirm({
            header: 'Existing User Conflict',
            message: this.identityCollisionMessage(collision),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Delete Old User',
            rejectLabel: 'Keep Editing',
            accept: () => this.resolveIdentityCollisionDelete(collision, mode)
        });
    }

    private resolveIdentityCollisionDelete(collision: IdentityCollisionDetails, mode: PartyQuickCreateMode): void {
        const targetCustomerId = (this.record as Customer | null)?.id ?? undefined;
        this.saving = true;
        this.customerService.resolveIdentityCollisionDelete({
            targetCustomerId,
            conflictingCustomerId: collision.existingCustomerId ?? undefined,
            conflictingFirebaseId: collision.existingFirebaseId ?? null
        }).pipe(finalize(() => this.saving = false)).subscribe({
            next: () => this.executeSave(mode),
            error: error => {
                const message = error?.error?.message || error?.message || 'Unable to delete the conflicting user.';
                this.errorMessage = message;
                this.failed.emit(message);
            }
        });
    }

    private identityCollisionMessage(collision: IdentityCollisionDetails): string {
        const fieldLabel = collision.field === 'phoneNumber'
            ? 'phone number'
            : collision.field === 'emailAddress'
                ? 'email address'
                : 'login account';
        const identityValue = collision.field === 'phoneNumber'
            ? collision.existingCustomerPhone
            : collision.field === 'emailAddress'
                ? collision.existingCustomerEmail
                : collision.existingFirebaseId;
        const recordSummary = collision.backendRecordExists
            ? `Existing backend user: ${collision.existingCustomerName || 'Unknown'}${collision.existingCustomerId ? ` (ID ${collision.existingCustomerId})` : ''}.`
            : 'No backend customer record was found for the conflicting Firebase user.';
        return `That ${fieldLabel} is already linked to another user${identityValue ? `: ${identityValue}` : ''}. ${recordSummary} `
            + 'Choose "Delete Old User" to remove the old backend/Firebase user and retry this save, or keep editing to change the contact details.';
    }

    private isCustomerLike(): boolean {
        return ['customer', 'employee', 'laborer', 'brokerContact', 'customsAgent'].includes(`${this.resolvedMode}`);
    }

    private errorLabel(): string {
        const mode = this.resolvedMode;
        if (mode === 'broker') return 'customs broker';
        if (mode === 'brokerContact') return 'broker contact';
        if (mode === 'shipper') return 'shipper';
        if (mode === 'transportationAgency') return 'transportation agency';
        if (mode === 'driver') return 'driver';
        if (mode === 'company') return 'company';
        if (mode === 'employee') return 'employee';
        if (mode === 'laborer') return 'laborer';
        if (mode === 'customsAgent') return 'customs agent';
        if (mode === 'location') return 'delivery location';
        return 'customer contact';
    }
}

interface IdentityCollisionDetails {
    code: string;
    field: string;
    backendRecordExists?: boolean | null;
    firebaseUserExists?: boolean | null;
    existingCustomerId?: number | null;
    existingCustomerName?: string | null;
    existingCustomerEmail?: string | null;
    existingCustomerPhone?: string | null;
    existingFirebaseId?: string | null;
}
